/**
 * SubscriptionSection - Subscription management component
 *
 * Shows active subscription details (PlanDetailsCard) for paying users,
 * upgrade CTA for free users, or sign-in prompt for anonymous users.
 * Also shows family plan and practitioner group management links.
 *
 * Agent: Diego <diego@liftping.net>
 * Task: NocoDB DD-156 (Subscription Management UI)
 */
import { View, Text, Pressable, ActivityIndicator } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { TierBody } from "@dramadetector/ui";
import { GlassCard } from "../../../components/glass";
import { SettingsItem } from "../../../components/SettingsItem";
import { PlanDetailsCard } from "./PlanDetailsCard";
import { useSubscription } from "../../../hooks/useSubscription";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { useSDUIConfig, toUISpecTier } from "@dramadetector/ui/json-render";
import { useAuth } from "../../../contexts/AuthContext";

interface SubscriptionSectionProps {
  /** Navigate to subscription screen */
  onManageSubscription: () => void;
  /** Navigate to family management screen (if family member) */
  onManageFamily?: () => void;
  /** Whether user is a family member */
  isFamilyMember: boolean;
  /** Whether user is a family admin */
  isFamilyAdmin: boolean;
  /** Family plan tier (drama_plus, dramax) */
  familyTier?: string;
  /** Navigate to practitioner groups management screen */
  onManagePractitionerGroups?: () => void;
  /** Number of practitioner groups user belongs to */
  practitionerGroupCount?: number;
  /** Open Polar billing portal */
  onOpenBillingPortal?: () => void;
}

export function SubscriptionSection({
  onManageSubscription,
  onManageFamily,
  isFamilyMember,
  isFamilyAdmin,
  familyTier,
  onManagePractitionerGroups,
  practitionerGroupCount,
  onOpenBillingPortal,
}: SubscriptionSectionProps) {
  const { t } = useTranslation('settings');
  const { t: tSubscription } = useTranslation('subscription'); // For family.tierBadge keys
  const { user, isAnonymous: authIsAnonymous } = useAuth();
  const { tier, isPremium } = useEntitlements();
  const { subscription, planName, isLoading, error, refetch: refetchSubscription } = useSubscription();

  // SDUI config for subscription page (controls DramaX plan visibility)
  const { config: subscriptionConfig } = useSDUIConfig('subscription-page', toUISpecTier(tier));

  // Phase 2 (NocoDB #3870): prefer AuthContext's derived `isAnonymous` (canonical)
  // over the `!user || tier === 'anonymous'` heuristic — the latter was fragile
  // to `useTier` result timing and didn't explicitly check BA anonymous sessions.
  const isAnonymous = authIsAnonymous || tier === 'anonymous';
  const hasActiveSubscription = !!subscription;

  // Determine if there's a CTA item to show inside the GlassCard
  // Note: DramaX upgrade is gated until all DramaX features are complete.
  // When showDramaxPlans is false, hide "Change Plan" for Drama+ subscribers
  // since there's no higher tier to upgrade to yet.
  const showDramaxPlans = subscriptionConfig.showDramaxPlans;
  const showCta = isAnonymous || (!isPremium && !hasActiveSubscription) || (hasActiveSubscription && showDramaxPlans && tier !== 'dramax');
  const showFamilyItem = isFamilyMember && !!familyTier && !!onManageFamily;
  const showPractitionerItem = (practitionerGroupCount ?? 0) > 0 && !!onManagePractitionerGroups;
  const showBillingItem = hasActiveSubscription && !!onOpenBillingPortal;
  const showGlassCard = showCta || showFamilyItem || showPractitionerItem || showBillingItem;

  // Loading state
  if (isLoading) {
    return (
      <View className="mt-4" testID="subscription-section">
        <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
          {t('sections.subscription')}
        </TierBody>
        <View className="items-center py-6">
          <ActivityIndicator size="small" color="#6B7280" />
        </View>
      </View>
    );
  }

  // Error state
  if (error) {
    return (
      <View className="mt-4" testID="subscription-section">
        <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
          {t('sections.subscription')}
        </TierBody>
        <View className="mx-4 bg-red-50 border border-red-200 rounded-xl p-4 items-center">
          <Text className="text-red-700 text-sm mb-2">
            {t('subscription_manage.load_error', { defaultValue: 'Could not load subscription details.' })}
          </Text>
          <Pressable onPress={() => refetchSubscription()} className="py-2 px-4 bg-red-100 rounded-lg">
            <Text className="text-red-700 text-sm font-medium">
              {t('subscription_manage.retry', { defaultValue: 'Retry' })}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View className="mt-4" testID="subscription-section">
      <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
        {t('sections.subscription')}
      </TierBody>

      {/* Failed Payment Banner (past_due status) */}
      {subscription?.status === 'past_due' && (
        <View className="mx-4 mb-4 bg-red-50 border border-red-200 rounded-xl p-4">
          <Text className="text-red-800 font-semibold mb-1">
            {t('billing.payment_failed_title', { defaultValue: 'Payment Failed' })}
          </Text>
          <Text className="text-red-600 text-sm mb-2">
            {t('billing.payment_failed_message', {
              plan: planName,
              defaultValue: `Update your payment method to keep your ${planName} access.`,
            })}
          </Text>
          {onOpenBillingPortal && (
            <Pressable onPress={onOpenBillingPortal}>
              <Text className="text-red-700 font-medium underline">
                {t('billing.update_payment', { defaultValue: 'Update Payment Method' })}
              </Text>
            </Pressable>
          )}
        </View>
      )}

      {/* Active Subscription: Show plan details with cancel */}
      {hasActiveSubscription && planName && (
        <View className="mb-2">
          <PlanDetailsCard
            subscription={subscription}
            planName={planName}
            onCancelled={refetchSubscription}
            onOpenBillingPortal={onOpenBillingPortal}
          />
        </View>
      )}

      {showGlassCard && (
        <GlassCard>
          {/* Manage / Upgrade / Sign-in CTA */}
          {/* Subscribed users: PlanDetailsCard above handles plan management (details + cancel).
              Only show upgrade option if they could move to a higher tier. */}
          {isAnonymous ? (
            <SettingsItem
              icon="🔑"
              label={t('subscription_manage.sign_in_title', { defaultValue: 'Sign in to manage' })}
              description={t('subscription_manage.sign_in_description', { defaultValue: 'Sign in to view and manage your subscription' })}
              onPress={onManageSubscription}
            />
          ) : isPremium ? (
            /* Paid users: Show "Change Plan" only if they can upgrade (not already on highest tier) */
            tier !== 'dramax' && showDramaxPlans ? (
              <SettingsItem
                icon="⬆️"
                label={t('subscription_manage.change_plan_title', { defaultValue: 'Change Plan' })}
                description={t('subscription_manage.change_plan_description', { defaultValue: 'View other plans and upgrade options' })}
                onPress={onManageSubscription}
              />
            ) : null
          ) : (
            /* Free users only: Show upgrade CTA */
            <SettingsItem
              icon="⭐"
              label={t('subscription_manage.upgrade_title', { defaultValue: 'Upgrade to Drama+' })}
              description={t('subscription_manage.upgrade_description', { defaultValue: 'Get unlimited analyses, longer texts, and more' })}
              onPress={onManageSubscription}
            />
          )}

          {/* Family Plan Management (conditional) */}
          {showFamilyItem && (
            <>
              {showCta && <View className="border-t border-gray-200" />}
              <SettingsItem
                icon="👨‍👩‍👧‍👦"
                label={t('family_plan.title')}
                description={
                  isFamilyAdmin
                    ? t('family_plan.admin_description')
                    : t('family_plan.member_description')
                }
                badge={
                  familyTier === 'drama_plus'
                    ? tSubscription('family.tierBadge.drama_plus')
                    : tSubscription('family.tierBadge.dramax')
                }
                onPress={onManageFamily}
                testID="family-plan-settings-item"
              />
            </>
          )}

          {/* Practitioner Groups Management (conditional) */}
          {showPractitionerItem && (
            <>
              {(showCta || showFamilyItem) && <View className="border-t border-gray-200" />}
              <SettingsItem
                icon="🩺"
                label={t('practitioner_groups.title')}
                description={t('practitioner_groups.description', { count: practitionerGroupCount })}
                onPress={onManagePractitionerGroups}
              />
            </>
          )}

          {/* Billing & Invoices (Polar Customer Portal) */}
          {showBillingItem && (
            <>
              {(showCta || showFamilyItem || showPractitionerItem) && <View className="border-t border-gray-200" />}
              <SettingsItem
                icon="🧾"
                label={t('billing.title', { defaultValue: 'Billing & Invoices' })}
                description={t('billing.description', { defaultValue: 'View invoices and update payment method' })}
                onPress={onOpenBillingPortal}
                testID="billing-invoices-button"
              />
            </>
          )}
        </GlassCard>
      )}
    </View>
  );
}
